import React, { useState, useEffect, useMemo } from "react";
import { Slider } from "./ui/slider";
import { motion } from "framer-motion";
import { Battery, Leaf, Clock, ArrowRight, Sparkles, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useCalculatorResult } from "../context/CalculatorResultContext";
import api from "../lib/api";

const SavingsCalculator = () => {
  const { t } = useLang();
  const { setCalculatorData } = useCalculatorResult();
  const [panels, setPanels] = useState([10]);
  const [usage, setUsage] = useState([4000]);
  const [cost, setCost] = useState([180]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const payload = useMemo(() => ({
    panels: panels[0],
    annual_usage_kwh: usage[0],
    monthly_cost: cost[0],
  }), [panels, usage, cost]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post("/calculator/estimate", payload);
        if (!cancelled) setResult(data);
      } catch (e) {
        if (!cancelled) setResult(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(id); };
  }, [payload]);

  // Publish payload + result so other sections (e.g. salderingsvergelijking)
  // can display personalised numbers without altering the calculator logic.
  useEffect(() => {
    setCalculatorData(payload, result);
  }, [payload, result, setCalculatorData]);

  const annual = result ? `€ ${Number(result.estimated_annual_savings).toLocaleString("nl-NL")}` : "—";
  const monthly = result ? `€ ${Number(result.monthly_savings).toLocaleString("nl-NL")}` : "—";
  const recommended = result ? Number(result.recommended_battery_kwh) : null;

  return (
    <section id="calculator" className="section-pad relative overflow-hidden" data-testid="savings-calculator">
      <div className="absolute inset-0 noise-bg opacity-50" />
      <div className="blob-pink absolute top-10 right-0 opacity-50" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">{t.calc.eyebrow}</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">{t.calc.title}</h2>
          <p className="mt-5 text-slate-600 text-lg">{t.calc.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 bg-white/80 backdrop-blur-2xl border border-white/60 shadow-[0_30px_80px_rgba(15,23,42,0.08)] rounded-[2rem] p-7 lg:p-10">
            <CalcSlider
              label={t.calc.panels}
              value={panels}
              onChange={setPanels}
              min={0} max={40} step={1}
              display={`${panels[0]}`}
              testid="calc-panels"
            />
            <CalcSlider
              label={t.calc.usage}
              value={usage}
              onChange={setUsage}
              min={1000} max={10000} step={100}
              display={`${usage[0].toLocaleString("nl-NL")} kWh`}
              testid="calc-usage"
            />
            <CalcSlider
              label={t.calc.cost}
              value={cost}
              onChange={setCost}
              min={50} max={500} step={10}
              display={`€ ${cost[0]}`}
              testid="calc-cost"
            />

            {/* Recommendation tile */}
            <div className="mt-9 rounded-2xl bg-brand-soft/60 border border-brand/15 p-5 flex items-center gap-4" data-testid="calc-recommendation">
              <div className="w-12 h-12 rounded-2xl gradient-pink text-white flex items-center justify-center flex-shrink-0">
                <Battery size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-brand font-bold">{t.calc.recommendationLabel}</div>
                <div className="font-heading font-black text-2xl text-navy-900 tabular-nums mt-0.5">
                  {recommended ? `${recommended} kWh` : "—"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{t.calc.recommendationHint}</div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 bg-navy-900 text-white rounded-[2rem] p-7 lg:p-9 relative overflow-hidden"
            data-testid="calc-results"
          >
            <div className="absolute -top-24 -right-16 blob-pink opacity-50" />
            <div className="relative z-10">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-2">{t.calc.results}</p>

              {/* Primary metric: annual savings */}
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-slate-300 font-semibold">{t.calc.annualSavings}</div>
                <div className={`font-heading font-black text-5xl sm:text-6xl mt-2 gradient-pink-text tabular-nums leading-none ${loading ? "opacity-60" : "opacity-100"} transition-opacity`} data-testid="calc-annual">
                  {annual}
                </div>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-sm" data-testid="calc-monthly">
                  <Sparkles size={14} className="text-brand" />
                  <span className="text-slate-200">{t.calc.monthlySavings}</span>
                  <span className="font-bold tabular-nums">{monthly}</span>
                </div>
              </div>

              {/* Secondary metrics — de-emphasized */}
              <div className="mt-7 grid grid-cols-2 gap-3">
                <SecondaryStat
                  icon={Clock}
                  label={t.calc.payback}
                  value={result ? `${result.payback_years} ${t.calc.years}` : "—"}
                  loading={loading}
                />
                <SecondaryStat
                  icon={Leaf}
                  label={t.calc.co2}
                  value={result ? `${Number(result.co2_savings_kg).toLocaleString("nl-NL")} ${t.calc.kg}` : "—"}
                  loading={loading}
                />
              </div>

              {/* Saldering 2027 info banner */}
              <a
                href="#saldering"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("saldering");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="group block mt-7 rounded-2xl border border-white/10 bg-white/[0.05] hover:bg-white/[0.08] backdrop-blur-xl p-4 transition-colors duration-300"
                data-testid="calc-saldering-banner"
              >
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-xl bg-brand/15 text-brand flex items-center justify-center flex-shrink-0 text-base leading-none">
                    🔋
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-white leading-relaxed">
                      Waarom bespaar je zoveel met een thuisbatterij?
                    </p>
                    <span className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand group-hover:gap-2 transition-all">
                      Bekijk wat er verandert vanaf 2027
                      <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </a>

              <Link
                to="/contact"
                state={{ calculator: { ...payload, ...(result || {}) } }}
                className="mt-5 w-full btn-pink !text-white"
                data-testid="calc-cta"
              >
                {t.calc.cta} <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const CalcSlider = ({ label, value, onChange, min, max, step, display, testid }) => (
  <div className="mt-7 first:mt-0" data-testid={`${testid}-row`}>
    <div className="flex items-center justify-between mb-3">
      <label className="text-sm font-semibold text-navy-900">{label}</label>
      <span className="text-sm font-bold text-brand tabular-nums">{display}</span>
    </div>
    <Slider value={value} onValueChange={onChange} min={min} max={max} step={step} data-testid={testid} />
  </div>
);

const SecondaryStat = ({ icon: Icon, label, value, loading }) => (
  <div className="rounded-xl bg-white/5 border border-white/10 p-3">
    <div className="flex items-center gap-2 text-slate-300">
      <Icon size={14} className="text-brand" />
      <span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span>
    </div>
    <div className={`font-heading font-bold text-lg tabular-nums mt-1 ${loading ? "opacity-60" : "opacity-100"} transition-opacity`}>
      {value}
    </div>
  </div>
);

export default SavingsCalculator;
